<!--
  (c) Copyright 2024 Calabrio, Inc.
  All Rights Reserved. www.calabrio.com LICENSED MATERIALS
  Property of Calabrio, Inc., Minnesota, USA

  No part of this publication may be reproduced, stored or transmitted,
  in any form or by any means (electronic, mechanical, photocopying,
  recording or otherwise) without prior written permission from Calabrio, Inc.
-->

<template>
  <!-- We need to load the top level div for iframe pages -->
  <!-- Add the iFrame class name under the style section -->
  <div v-loading="iFramePageLoading" id="app" :class="'wy-brand wy-product-'+config.appID+ ' wy-'+$router.currentRoute.name+'-page'+`${isC1Auth ? ' wy-c1-auth' : ''}`" :rel="`B:${$root.gitBranch}, V:${$root.gitCommitHash}`">
    <router-view v-if="isIFramePage"/>
    <div v-else-if="isPrivatePage">
      <el-container class="wy-container" v-if="isLoggedIn">
        <el-header class="wy-container-header" style="height: 75px;">
          <nav-top ref="navTop" />
        </el-header>
        <el-container class="wy-container-body">
          <el-aside class="wy-container-nav">
            <nav-left ref="navLeft"/>            
          </el-aside>
          <el-main ref="wyContainerMain" class="wy-container-main" element-loading-text="Loading..." v-loading="pageLoading">
            <div ref="clickOverlayModal" id="clickOverlayModal" :style="{display: (clickOverlayModalData.active ? 'block' : 'none')}" />
            <router-view/>
          </el-main>
        </el-container>
        <el-footer class="wy-container-footer">
          <wy-footer/>
        </el-footer>
      </el-container>      
    </div>
    <router-view v-else/>
    <span id="spanPlaceholder" style="visibility: hidden"/>

    <el-popover
      ref="tooltip"
      placement="bottom"
      :width="tooltipWidth"
      popper-class="wy-popover-tooltip"
      trigger="manual">
      <span v-html="tooltipContent"></span>
    </el-popover>

    <div class="wy-app-modal" v-if="appModalComponent">
      <div class="wy-app-modal-content" :style="appModalComponent.modelContentStyle">
        <div v-if="appModalComponent.modalTitle" class="wy-app-modal-header">
          <div class="wy-app-modal-header-box">
            <span class="wy-app-modal-header-title">{{appModalComponent.modalTitle}}</span>
            <span class="wy-app-modal-header-close el-icon-close" :class="{'disabled': isDisabled}" @click="handleAppModalClose" />
            <div style="clear: both" />
          </div>
        </div>
        <div class="wy-app-modal-body">
          <KeepAlive>
            <component ref="appModalRef" :appVue="controller" :is="appModalComponent" :options="appModalComponent.options"></component>
          </KeepAlive>
        </div>
      </div>
    </div>
    <div class="site-tooltips">tooltips content</div>
    <div class="benchmark-tooltips">tooltips content</div>
    <wy-process-error-dialogue />
    <wy-process-wait-dialogue />
    <wy-process-list-drawer :controller="controller" v-if="isLoggedIn"/>
  </div>
</template>

<script>
import Vue from 'vue';
import { mapGetters, mapMutations } from 'vuex';
import axios from 'axios';
import moment from 'moment';
import NavTop from './views/NavTop.vue';
import NavLeft from './views/NavLeft.vue';
import Footer from './views/Footer.vue';
import ProcessErrorDialogue from './views/ProcessErrorDialogue.vue';
import ProcessWaitDialogue from './views/ProcessWaitDialogue.vue';
import ProcessListDrawer from './views/ProcessListDrawer.vue';

export default Vue.extend({
  name: 'app',
  components: {
    'nav-top': NavTop,
    'nav-left': NavLeft,
    'wy-footer': Footer,
    'wy-process-error-dialogue': ProcessErrorDialogue,
    'wy-process-wait-dialogue': ProcessWaitDialogue,
    'wy-process-list-drawer': ProcessListDrawer,
  },

  data() {
    return {
      locationHsh: window.location.hash,
      controller: this,
      pageLoading: false,
      iFramePageLoading: false,
      loading: false,
      tooltipWidth: null,
      hidePopper: true,
      tooltipContent: null,
      appModalComponent: null,
      appModalStack: [],
      clickOverlayModalData: {
        active: false,
        handleApply: null
      },
      preMsgObj: null,
      isDisabled: false
    }
  },

  timers: {
    refreshAccessToken: { time: 60000 * 2, repeat: true, autostart: true }, // 1 minutes
    //refreshProcessList: { time: 60000 * 1, repeat: true, autostart: true }, // 1 minutes
  },

  computed: {
    ...mapGetters(['isC1Auth', 'isLoggedIn', 'isC1BotAnaltyics', 'isC1Interactions', 'isC1AutoQA', 'isC1SmartTags', 'config', 'isRoleAllow', 'authUser', 'dataModel', 'moreFiltersCounter', 'noAvaliableData']),
    
    pageName() {
      return 'page-' + this.$router.currentRoute.name;
    },
    isPrivatePage() {
      return  (this.$route.name && !this.$route.meta.pub);
    },
    isIFramePage() {
      return this.$route.meta.isIFramePage
    }
  },
  
  methods: {
    ...mapMutations(['updataNoDataType', 'updateNoAvaliableData']),

    async refreshAccessToken() {    
      if(this.isLoggedIn && !this.isC1Auth) {
        // get new token
        let isOK = await this.$store.dispatch('refreshAccessToken');
        if (!isOK) {
          this.logout('sessionExpired', 3000);
        }
      }
    },

    async refreshProcessList() {
      if(this.isLoggedIn && !this.isIFramePage) {
        if(this.isC1BotAnaltyics) {
          if(this.isRoleAllow([900005, 900009])) {
            await this.$store.dispatch('fetchProcessList', {showAlert: true});
          }
        }
        else if(this.isC1Interactions) {
          if(this.isRoleAllow([4604])) {
            // no process data needed since the rename/merge is done on the fly
            await this.$store.dispatch('fetchProcessList', {showAlert: true});
          }
        }
        else if(this.isC1AutoQA) {
          if(this.isRoleAllow([4704])) {
            // no process data needed since since no rename/merge actions
            await this.$store.dispatch('fetchProcessList', {showAlert: true});
          }
        }
        else if(this.isC1SmartTags) {
          if(this.isRoleAllow([4903])) {
            // no process data needed since since no rename/merge actions
            await this.$store.dispatch('fetchProcessList', {showAlert: true});
          }
        }
        else if(this.isRoleAllow([3001, 4201])) {
          await this.$store.dispatch('fetchProcessList', {showAlert: true});
        }
      }
    },

    handleYesContinueSession(e) {
      e.preventDefault();
      e.stopPropagation();
      this.refreshAccessToken();
    },

    handleNoContinueSession(e) {
      e.preventDefault();
      e.stopPropagation();
      this.logout('manual');
    },

    logout(mode, delay=500) {
      this.$store.dispatch('logout', mode);

      if(mode == 'sessionExpired') {
        this.$root.showNotificationLocale("error", "login.sessionExpiredTitle", "login.sessionExpiredMsg", {duration: delay});
      }
      else if(mode == 'invalidSession') {
        this.$root.showNotificationLocale("error", "login.invalidSessionTitle", "login.invalidSessionMsg", {duration: delay});
      }
      setTimeout(() => {
        this.$router.replace({name: 'logout'});
      }, delay);
    },

    showTooltip(e) {
      e.preventDefault();
      e.stopPropagation();

      if(this.$refs.tooltip) {
        if(!this.$refs.tooltip.showPopper) {
          let tooltipKey = e.target.getAttribute('wy-tooltip-key');
          let tooltipContent = e.target.getAttribute('wy-tooltip-content');
          let width = e.target.getAttribute('wy-tooltip-width') ? e.target.getAttribute('wy-tooltip-width') : this.tooltipWidth;

          this.$refs.tooltip.referenceElm = e.target;
          if(tooltipKey && tooltipKey != '') {
            this.tooltipContent = this.$t(`tooltips.${tooltipKey}`)
          }
          else {
            this.tooltipContent = tooltipContent;
          }
          this.tooltipWidth = width;
          this.$refs.tooltip.doShow();
        }
        else {
          this.$refs.tooltip.doClose();
          this.tooltipContent = null;
        }   
      }
    },

    hideTooltip(e) {
      e.preventDefault();
      e.stopPropagation();

      if(this.$refs.tooltip) {
        this.$refs.tooltip.doClose();
        this.tooltipContent = null;
      }
    },

    handleClickEvent(e) {
      if(e) {
        e.stopPropagation();

        if(this.$refs.tooltip) {
          this.$refs.tooltip.doClose();
          this.tooltipContent = null;
        }

        if(this.clickOverlayModalData.active) {
          if(this.clickOverlayModalData && this.clickOverlayModalData.handleApply) {
            this.clickOverlayModalData.handleApply();
          }
          
          setTimeout(() => { this.clickOverlayModalData.active = false; }, 200)
        }
      }

      this.$root.$emit(`fire-app-hide-popover-events`, e) //el-popover
    },

    async handleAppModalOpen(name, options) {
      let component = this.$root.requireComponent("."+name);
      this.isDisabled = false;
      this.appModalComponent = component.default;
      this.appModalComponent.options = options;
      this.appModalStack.push(this.appModalComponent);
      if(this.$router.app.hideNotification) this.$router.app.hideNotification();
    },

    async handleAppModalClose() {
      if(!this.$refs.appModalRef.hasNoSavedChanges) {
        if(this.$refs.appModalRef.beforeModalClose) {
          this.isDisabled = true;
          await this.$refs.appModalRef.beforeModalClose();
        }
        // close modal
        this.appModalStack.pop();
        if(this.appModalStack.length > 0) {
          this.appModalComponent = this.appModalStack[this.appModalStack.length-1];
        }
        else {
          this.appModalComponent = null;
        }

        if(this.$refs.appModalRef.afterModalClose) {
          await this.$refs.appModalRef.afterModalClose();
        }
      }
      else {
        this.$refs.appModalRef.handleUnSavedChanges(null, this.$refs.appModalRef.activeTab, this.handleAppModalClose)
      }
    },

    containerMainScrollHander() {
      this.$root.$emit(`fire-container-main-scroll-events`);
    },

    formatDate(inputDate){
      var date = new Date(inputDate);
      var year = date.toLocaleString("default", { year: "numeric" });
      var month = date.toLocaleString("default", { month: "2-digit" });
      var day = date.toLocaleString("default", { day: "2-digit" });
      return year + "-" +  month + "-" + day;
    },

    handleNoDataChecking(response) {
      if(this.preMsgObj) {
        this.preMsgObj.close();
      }
      let hasNoData = response && response.data && (
                    response.data.results && response.data.results.length == 0 || //contac reason
                    response.data.trend && response.data.trend.length == 0 || 
                    response.data.modelStats && response.data.maxHandlingTime == -1 && response.data.minHandlingTime == -1 && response.data.maxResponseTime == -1 
                  );
      if(this.$router.history.current.name == 'responsePerformance') hasNoData = false;
      let dateWidget = this.$refs.navTop.$refs["date-widget"]
      this.updateNoAvaliableData(false);
      if(hasNoData) {
        if(dateWidget) {
          if(this.dataModel.appliedFiltersCounter == 0) {
            this.updataNoDataType(1)
            let maxDate = new Date(this.dataModel.dateMax);
            let dateStartMoment = moment(this.dataModel.dateStart,'YYYY-MM-DD');
            let dateEndMoment = moment(this.dataModel.dateEnd,'YYYY-MM-DD');
            let dateRangeDatesDiff = dateEndMoment.diff(dateStartMoment, 'days', true);  
            let dateStart = this.formatDate(maxDate.setDate(maxDate.getDate() - (dateRangeDatesDiff - 1)))
            let dateEnd = this.dataModel.dateMax;
            this.updateNoAvaliableData(true)
            setTimeout(()=>{
              if(!this.preMsgObj) {
                this.preMsgObj = this.$message({
                  dangerouslyUseHTMLString: true,
                  duration: 50000000,
                  showClose: true,
                  customClass: 'no-data-msg',
                  message: `
                    <div class="first-div">Data is not available for the date range: ${moment(this.dataModel.dateStart,'YYYY-MM-DD').format("MMM Do, YYYY")} to ${moment(this.dataModel.dateEnd,'YYYY-MM-DD').format("MMM Do, YYYY")}</div> 
                    <div class="second-div">Please try this available date range: ${moment(dateStart,'YYYY-MM-DD').format("MMM Do, YYYY")} to ${moment(dateEnd,'YYYY-MM-DD').format("MMM Do, YYYY")}</div> 
                    <div class="toAvailavleDateRange" ref="toAvailavleDateRange">Go to available date range</div>`,
                  onClose: ()=>{
                    this.preMsgObj = null;
                  }
                });
                setTimeout(() => {
                  let leftMenuEl = document.querySelector(".wy-container-nav");
                  let leftMenuElWidth = 0
                  if(leftMenuEl) {
                    leftMenuElWidth = leftMenuEl.getBoundingClientRect().width;
                  }
                  this.preMsgObj.$el.style.left = this.preMsgObj.$el.offsetLeft + leftMenuElWidth/2 +'px'
                }, 0);
                let toAvailavleDateRange = document.querySelector(".toAvailavleDateRange");
                if(toAvailavleDateRange) {
                  toAvailavleDateRange.addEventListener('click', ()=>{
                    if(this.preMsgObj) {
                      this.preMsgObj.close();
                    }
                    this.dataModel.dateStart = dateStart;
                    this.dataModel.dateEnd = dateEnd;
                    dateWidget[0].dataModel.dateRangeType = -1
                    dateWidget[0].$refs.calendarPicker.selectDateRange(this.dataModel.dateStart, this.dataModel.dateEnd);
                    this.$root.fireEvent({name: 'handleFilterChange'});
                  })
                }
                this.$root.preMsgObj = this.preMsgObj;
              }
            }, 500)
          }
          else {
            this.updataNoDataType(2)
          }
        }
      }
    },
  },

  beforeCreate() {
    let vm = this;
  
    // request interceptor
    axios.interceptors.request.use(config => {
      config.validateStatus = function (status) {
        return (status == 200 || status == 202 || status == 204 || status == 400 || status == 401 || status == 402 || status == 404 || status == 409 || status == 474 || status == 470 || status == 475 || status == 501 || status == 502 || status == 503 || status == 504 || status == 505);
      }

      const demoConfigs = this.$store.getters.companyConfig.demoConfigs || {};
      if(demoConfigs && demoConfigs.isDemo) {
        config.headers['demo'] = demoConfigs.isDemo;
      }

      return config;
    }, (error) => {
      this.$router.app.setLoading(false);
      
      return Promise.reject(error);
    });
    
    // response interceptor
    axios.interceptors.response.use(async response => {
      if(this.$refs.navTop) {
        this.handleNoDataChecking(response);
      }
      // for each API request
      if(response.status == 402) {
        // if no permission to access this api call, show a message alert
        vm.$root.showNotificationLocale("error", 'login.invalidPermissionsTitle', 'login.invalidPermissionsMsg');
        return response;
      }
      else if(response.status == 401 || (response.status == 400 && response.data && response.data.errorCode == 4003)) { 
        // if not authrozied or no session, send user to login
        if(vm.isLoggedIn) {
          if(response.status == 401 && this.isC1Auth) {
            vm.$root.showNotificationLocale("error", 'apiAccessDenied.title', 'apiAccessDenied.message', {duration: 6000});
          }
          else if(response.data && response.data.errorCode == 4004) {
            vm.$root.showNotificationLocale("error", 'apiAccessDenied.title', 'apiAccessDenied.message', {duration: 6000});
          }
          else if(response.data && response.data.errorCode == 4002) {
            this.logout('invalidSession', 3000);
          }
          else {
            this.logout('sessionExpired', 3000);
          }
        }
        return response;
      }
      else {
        if(response.status == 200) {
          // if a new awt token return, update the authUser object
          const refreshedToken = response.headers['refreshed-token'];
          if(refreshedToken) {
            await this.$store.dispatch('updateAuthUserToken', refreshedToken);
          }
        }
        return response;
      }      
    }, (error) => {
      this.$router.app.setLoading(false);
      
      if(!axios.isCancel(error)) {
        console.error(error);
        vm.$root.showNotificationLocale("error", 'error.serverErrorTitle', 'error.serverErrorMsg');
      }
      else {
        console.log('Request cancelled')
      }
      //return Promise.reject(error);
      return {};
    });    
  },

  mounted() {
    if(this.$refs.wyContainerMain && this.$refs.wyContainerMain.$el) {
      this.$refs.wyContainerMain.$el.addEventListener("scroll", () => {
        this.$root.$emit(`fire-app-hide-popper-events`, this.hidePopper) //el-dropdown
      });
    }
    
    this.$root.registerTooltips = (el) => {
      setTimeout(() => {
        let tooltipList = el.querySelectorAll('*[wy-tooltip-content],*[wy-tooltip-key]');
        tooltipList.forEach(el => {
          let mode = el.getAttribute('wy-tooltip-mode');
          if(mode && mode == "hover") {
            el.addEventListener('mouseover', this.showTooltip)
            el.addEventListener('mouseout', this.hideTooltip)
          }
          else {
            el.addEventListener('click', this.showTooltip)
          }
        })
      }, 1000)
    }

    this.$root.unregisterTooltips = (el) => {
      let tooltipList = el.querySelectorAll('*[wy-tooltip-content],*[wy-tooltip-key]');
      tooltipList.forEach(el => {
        el.addEventListener('mouseover', this.showTooltip)
        el.addEventListener('mouseout', this.hideTooltip)
        el.addEventListener('click', this.showTooltip)
      })
    }

    document.addEventListener('click', this.handleClickEvent);

    // listent app modal event
    this.$root.$on(`fire-app-modal-events`, (name, options) => {
      this.handleAppModalOpen(name, options);
    });

    // listent clickOverlayModal event fire from other modules
    this.$root.$on(`fire-app-clickOverlayModal-events`, ({handleApply, active}) => {
      if(this.preMsgObj) {
        this.preMsgObj.close();
      }
      if(active) {
        this.clickOverlayModalData.active = active;
        this.clickOverlayModalData.handleApply = handleApply;
        if(this.$refs.clickOverlayModal) {
          this.$refs.clickOverlayModal.addEventListener('click', this.handleClickEvent);
        }
      }
      else {
        if(this.$refs.clickOverlayModal) {
          this.$refs.clickOverlayModal.removeEventListener('click', this.handleClickEvent);
          this.clickOverlayModalData.handleApply = handleApply;
          setTimeout(() => { this.clickOverlayModalData.active = false; }, 200)
        }
      }
    });

    // add scrolling listener
    if(this.$refs.wyContainerMain) {
      this.$refs.wyContainerMain.$el.addEventListener('scroll', this.containerMainScrollHander);
    }
  },

  async created() {
    this.$root.initApp();
    if(!this.isC1Auth){
      this.refreshAccessToken();
    }
  },
});
</script>

<style lang="scss">
@import './css/imports.scss';

html {
  height: 100%;
  overflow: hidden;
}
body {
  position:relative;
  margin: 0;
  padding: 0;
  height: 100%;
  background-color: #ffffff;
  font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;
  font-size: 16px;
  letter-spacing: 0.07px;
  color: var(--site-font-primary-color);

  .no-result-found {
    height: 427px !important;
    width: 100% !important;
    .no-data {
      text-align: center;
      display: block;
      border: none;
      height: 139px;
      // width: 100%;
      margin-top: 10%;
      background-position-x: 50%;
      background-repeat: no-repeat;
      // background-size: 36%;
      background-image: url('./assets/icons/icon_no_data.svg');
    }
    .no-data-title {
      // width: fit-content;
      width: 100%;
      // margin-left: 35%;
      margin-top: 10px;
      text-align: center;
      font-size: 21px;
      color: var(--site-font-primary-color) !important;
      font-weight: 600;
    }
    .no-data-content {
      width: 100%;
      line-height: 20px;
      font-size: 14px;
      font-weight: 500;
      text-align: center;
      color: var(--site-font-primary-color) !important;
      .content-text {
        width: 285px;
        text-align: center;
        display: inline-flex;
        margin-top: 20px;
      }
    }
  }
}

// add iFrame page class names here  (TODO: find a better way to handle this)
#app.wy-null-page, 
#app.wy-aiEvaluation-page,
#app.wy-aiEvaluationSummary-page {
  .el-loading-mask {
    opacity: 1;
    background-color: #FFF;
    height: auto;
    width: auto;

    .el-loading-spinner {
      top: 38%;
      background: var(--site-loading-icon);
      background-repeat: no-repeat;
      background-size: 25px;
      background-position: center;    
      .circular {
        display: inline-block;
        width: 90px;
        height: 90px;
      }
      .path {
        -webkit-animation: loading-dash 2s ease-in-out infinite;
        animation: loading-dash 2s ease-in-out infinite;
        stroke-dasharray: 90,150;
        stroke-dashoffset: 0;
        stroke-width: 2.5 !important;
        stroke: #0C2837 !important;
        stroke-linecap: round; 
      }
      .el-loading-text {
        display: none;
      }
    }
  }
}

#app.wy-aiEvaluationSummary-page {
  background-color: #fafafa;
  height: 99%;
}


#app.wy-flows-page {
  .component.brands-widget {
    top: -31px !important;
    border-right: 0px #C9D3DA solid !important;
  }
  .wy-container-main {
    padding: 40px 0px 0px 0px !important;
  }
}
#app.wy-compareMetrics-page {
  .component.brands-widget {
    top: -1px !important;
    border-right: 0px #C9D3DA solid !important;
  }
  .platform-dropdown-widget {
    top: -1px !important;
    border-right: 0px #C9D3DA solid !important;
  }
  .component.filtering-widget {
    top: -18px !important;
    border-right: 0px #C9D3DA solid !important;
  }
}

#app.wy-contactReasonsPage-page, #app.wy-smartTagDashboard-page {
  min-width: 1200px;
  .v-modal {
    background: unset !important;
  }
  .wy-container-main{
    overflow: hidden !important;
  }
  .wy-header-menu-filtering.interactions {
    width: 100%;
    min-width: 90px;
    margin-right: 10px;
    padding-right: 25px;
  }
}
#app.wy-contactReasonsPage-page.contactReasonDetail {
  min-width: unset;
}
#app.wy-smartTagDashboard-page.smartTagDashboardDetail {
  min-width: unset;
}
#app.wy-agentOptimization-page, #app.wy-performanceComparison-page, 
#app.wy-intentPerformance-page, #app.wy-responsePerformance-page,
#app.wy-contactReasonsPage-page, #app.wy-home-page, #app.wy-smartTagDashboard-page {
  .wy-container-main{
    overflow-x: hidden;
    // background-color: #F3F6F8 !important;
    background-color: #FFF !important;
    &::-webkit-scrollbar {
      width: 20px;
    }
    &::-webkit-scrollbar-thumb {
      border-radius: 160px;
      border: 2px solid transparent;
      border-top: 4px solid transparent;
      background-clip: content-box;
      background-color: #CAD2DA;
    }
    &::-webkit-scrollbar-track {
      background:#F3F6F8;
      border-top: 1px solid #CAD2DA;
    }
    &::-webkit-scrollbar:vertical {
      width: 14px;
    }
    &::-webkit-scrollbar-thumb:vertical {
      border-radius: 160px;
      border: 2px solid transparent;
      border-left: 4px solid transparent;
      border-right: 4px solid transparent;
      background-clip: content-box;
      background-color: #CAD2DA;
    }
    &::-webkit-scrollbar-track:vertical {
      // border-left: 1px solid #CAD2DA;
      border-top: none !important;
    }
  }
}
.el-tooltip__popper.is-dark {
  background-color: #303133 !important;
  z-index: 99999 !important;
}
#app {  
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: left;
  color: var(--site-font-primary-color);
  height: 100%;
  width: 100%;

  .large-font {
    letter-spacing: 0.4px;
  }

  > .el-loading-mask {
    height: unset !important;
    opacity: 1;
    background-color: #FFF;
    .el-loading-spinner {
      top: 35%;
      background: var(--site-loading-icon);
      background-repeat: no-repeat;
      background-size: 25px;
      background-position: center;    
      .circular {
        display: inline-block;
        width: 90px;
        height: 90px; 
      }
      .path {
        -webkit-animation: loading-dash 2s ease-in-out infinite;
        animation: loading-dash 2s ease-in-out infinite;
        stroke-dasharray: 90,150;
        stroke-dashoffset: 0;
        stroke-width: 2.5 !important;
        stroke: #0C2837 !important;
        stroke-linecap: round; 
      }  
      .el-loading-text { 
        display: none;
      }
    }
    .el-loading-spinner .circular {
      display: none;
    }
  }

  > div {
    height: 100%;
    > .wy-container {
      height: 100% !important;
      overflow-y: hidden;
      overflow-x: hidden;

      .wy-container-header {
        padding: 0 0 0 0;
        margin: 0;
        // border-bottom: 1px solid #f3f3f3;
        // min-width: 1430px;
        //max-width: 1430px;
        width: 100%;
      }
      
      .wy-container-body {
        height: calc(100vh - 80px);
        background-color: #fbfdff;
        .wy-container-nav {
          overflow: hidden;
          background-color: #fff;
          text-align: left;
          // width: 20% !important;
          height: 100%;
          width: 288px;
          position: relative;
          top: -2px;
        }
        .wy-container-main:has(.wy-interactions-contact-reasons) {
          overflow-x: hidden;
        }
        .wy-container-main {
          background-color: #FFF;
          // overflow-x: hidden;
          overflow-y: auto;
          // height: 100%;
          padding: 15px 23px 0px 26px;
          position: relative;
          bottom: var(--site-main-container-bottom);
          &::-webkit-scrollbar {
            width: 20px;
          }
          &::-webkit-scrollbar-thumb {
            border-radius: 160px;
            border: 3px solid transparent;
            border-top: 5px solid transparent;
            background-clip: content-box;
            background-color: #CAD2DA;
          }
          &::-webkit-scrollbar-track {
            background:#F3F6F8;
            border-top: 1px solid #CAD2DA;
          }
          &::-webkit-scrollbar:vertical {
            width: 14px;
          }
          &::-webkit-scrollbar-thumb:vertical {
            border-radius: 160px;
            border: 2px solid transparent;
            border-left: 4px solid transparent;
            border-right: 4px solid transparent;
            background-clip: content-box;
            background-color: #CAD2DA;
          }
          &::-webkit-scrollbar-track:vertical {
            // border-left: 1px solid #CAD2DA;
            border-top: none !important;
          }
          .disable-page-mask {
            opacity: 1;
            height: calc(100vh - 80px - 20px);
            display: none;
          }
          .disable-page-mask.show{
            display: block;
          }

          .el-loading-mask {
            opacity: 1;
            background-color: #FFF;
            height: auto;
            width: auto;
            .el-loading-spinner {
              top: 38%;
              background: var(--site-loading-icon);
              background-repeat: no-repeat;
              background-size: 25px;
              background-position: center;    
              .circular {
                display: inline-block;
                width: 90px;
                height: 90px;
              }
              .path {
                -webkit-animation: loading-dash 2s ease-in-out infinite;
                animation: loading-dash 2s ease-in-out infinite;
                stroke-dasharray: 90,150;
                stroke-dashoffset: 0;
                stroke-width: 2.5 !important;
                stroke: #0C2837 !important;
                stroke-linecap: round; 
              }
              .el-loading-text {
                // padding-top: 81px;
                // color: #002852;
                // margin: 3px 0;
                // font-size: 20px;
                // font-weight: 500;
                display: none;
              }
            }
          }
        }
      }

      .wy-container-footer {
        display: none;
        height: 28px !important;
        // min-width: 1430px;
        // max-width: 1430px;
      }
    }
  }

  .session-expire-reminder {
    > .el-dialog {
      padding: 10px 10px 10px 17px;
    }
    .el-dialog__header {
      background-image: url('./assets/icons/alert_history.svg');
    }
  }

  .login-password-hints {
    margin-top: 20px;
    text-align: left;
    .title {
      font-size: 15px;
      font-weight: bold;
      margin-bottom: 5px;
    }
    .desc {
      font-size: 12px;        
    }
  }

  .login-layout {
    width: 100%;
    height: 100%;
    min-width: 960px;
    border-spacing: 0;
    padding: 0;

    .login-studio-logo {
      background-image: url('./assets/images/app_studio_rev.png');
      background-repeat: no-repeat;
      background-position: 0px 0px;
      background-size: 88%;
      width: 270px;
      height: 63px;
      position: absolute;
      left: 44px;
      top: 39px;    
    }
    
    .login-left {
      // background-image: url('./assets/images/login_bg.png');
      background-image: url('./assets/images/welcome_image.png');
      background-position-x: -5px !important;
      background-size: cover;
      width: 50%;
      min-width: 480px;
      min-height: 454px;
    }
    .login-right {
      width: 50%;
      min-width: 480px;
      min-height: 454px;
      vertical-align: top;

      .login-content {
        margin: auto;
        margin-top: 95px;
        width: 430px;
        border: 0px solid #ddd;
        text-align: left;              
      }
      .wa-login-page__codeForm {
        padding-top: 30px;
        .title {
          text-align: left;
          padding-bottom: 15px;
          font-size: 17px;
        }
        .input-confirm-code.el-input {
          padding-bottom: 10px;
        }
        .entercode {
          display: inline-block;
          margin: 28px 0px;
          padding: 10px;
          border: 1px #ccc solid;
        }
        .details {
          color: #666;
          font-size: 12px;
          margin-bottom: 15px;
        }
        .qrCode {
          clear: both;
          font-size: 14px;
          padding-top: 20px;
        }
      }
    }
  }

  #clickOverlayModal {
    position: fixed;
    display: none;
    top: 0px;
    left: 0px;
    right: 0;
    bottom: 0;
    background-color: var(--overlay-modal-background-color);
    z-index: 999;
    outline-style: none;
    user-select: none;
    -webkit-user-select: none; /* Chrome/Safari */ 
    -moz-user-select: none; /* Firefox */
    -ms-user-select: none; /* IE10+ */
  }
  .site-tooltips,.benchmark-tooltips {
    position: absolute;
    display: none;
    height: auto;
    padding: 5px 10px !important;
    max-width: 350px;
    width: max-content;
    white-space: pre-wrap;
    word-wrap: break-word;
    font-size: 14px !important;
    border: 1px solid #E6E6E6;
    // background-color: #E6E6E6;
    // color:  #2A2E32;
    background-color: #303133;
    font-weight: 500;
    color: #FFF;
    z-index: 9999;
    line-height: 18px;
    border-radius: 5px;

    animation: fadeIn .5s;
    -webkit-animation: fadeIn .5s;
    -moz-animation: fadeIn .5s;
    -o-animation: fadeIn .5s;
    -ms-animation: fadeIn .5s;
  }
  .site-tooltips:after,
  .site-tooltips:before{
    bottom: 100%;
    border: solid transparent;
    content: " ";
    height: 0;
    width: 0;
    position: absolute;
    pointer-events: none;
  }
  .benchmark-tooltips:after,
  .benchmark-tooltips:before {
    top: 27px;
    transform: rotateZ(180deg);
    border: solid transparent;
    content: " ";
    height: 0;
    width: 0;
    position: absolute;
    pointer-events: none;
  }

  .benchmark-tooltips.arrow-down:before {
    bottom: unset;
    top: 27px;
    transform: rotateZ(180deg);
  }
  // 'top: 27px; transform: rotateZ(180deg); bottom:unset;

  .site-tooltips:after,.benchmark-tooltips:after {
    border-color: rgba(255, 255, 255, 0);
    border-bottom-color: #303133;
    border-width: 5px;
    left: 50%;
    margin-left: -5px;
  }

  .arrow_box:before {
    border-color: rgba(113, 158, 206, 0);
    border-bottom-color: #719ECE;
    border-width: 5px;
    left: 50%;
    margin-left: -5px;
  }

  .site-tooltips.right_1:after {
    border-color: rgba(255, 255, 255, 0);
    border-bottom-color: #303133;
    border-width: 5px;
    left: unset;
    right: 75px !important;
    margin-left: -5px;
  }
  .site-tooltips.right_2:after {
    border-color: rgba(255, 255, 255, 0);
    border-bottom-color: #303133;
    border-width: 5px;
    left: unset;
    right: 42px !important;
    margin-left: -5px;
  }
  .site-tooltips.right_header_1:after {
    border-color: rgba(255, 255, 255, 0);
    border-bottom-color: #303133;
    border-width: 5px;
    left: unset;
    right: 69px !important;
    margin-left: -5px;
  }
  .site-tooltips.right_header_2:after {
    border-color: rgba(255, 255, 255, 0);
    border-bottom-color: #303133;
    border-width: 5px;
    left: unset;
    right: 35px !important;
    margin-left: -5px;
  }
  
}
.nodata-warning-msg {
  background: #FFFFFF 0% 0% no-repeat padding-box;
  box-shadow: 3px 3px 12px #00000029;
  border: 2px solid #005EB8;
  border-radius: 10px;
  opacity: 1;
  background-image: url('./assets/icons/alert_timed_out.svg');
  background-size: 9%;
  background-position-x: 17px;
  background-position-y: 11px;
  padding: 23px 15px 19px 24px;
  .el-message__icon {
    display: none;
  }
  .el-message__content {
    padding-left: 40px;
    font-size: 15px;
  }

  .el-message__closeBtn.el-icon-close {
    top: 24px;
  }
}

.failed-warning-msg {
  background: #395167 0% 0% no-repeat padding-box;
  box-shadow: 3px 3px 12px #00000029;
  border: 2px solid #666a6f;
  border-radius: 10px;
  opacity: 1;
  background-image: url('./assets/icons/alert_timed_out.svg');
  background-size: 9%;
  background-position-x: 17px;
  background-position-y: 11px;
  padding: 23px 15px 19px 24px;
  .el-message__icon {
    display: none;
  }
  .el-message__content {
    padding-left: 40px;
    font-size: 15px;
    color: #FFF;
  }

  .el-message__closeBtn.el-icon-close {
    top: 24px;
  }
}

ul.no-data-warning-items {
  font-size: 17px;
  color: #214586;
  padding-left: 0;
  margin: 0 0 5px 0;

  > div {
    padding-bottom: 10px;
  }

  > li {
    font-size: 14px;
    color: #909399;
    padding-top: 4px;
    padding-bottom: 6px;
    max-width: 340px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.prevent-select {
  -webkit-user-select: none; /* Safari */
  -ms-user-select: none; /* IE 10 and IE 11 */
  user-select: none; /* Standard syntax */
}

.wy-app-modal {
  position: fixed; /* Stay in place */
  left: 0;
  top: 0;
  width: 100%; /* Full width */
  height: 100%; /* Full height */
  overflow: auto; /* Enable scroll if needed */
  background-color: rgb(0,0,0); /* Fallback color */
  background-color: rgba(0,0,0,0.4); /* Black w/ opacity */
  z-index: 2000;    
  display: flex; /* Make wp-app-modal-content centre vertically */

  .wy-app-modal-content {
    position: relative;  
    margin: auto;
    box-sizing: border-box;
    width: 90%;
    max-width: 1900px;
    height: 80%;
    // max-height: 685px;
    background: #FFFFFF 0% 0% no-repeat padding-box;
    box-shadow: 0px 0px 6px #00000014;
    border-radius: 10px;
    
    .wy-app-modal-header {
      border-bottom: 1px solid #dfdfdf;

      .wy-app-modal-header-box {
        padding: 20px 20px 20px 20px;
        
        .wy-app-modal-header-title {
          font-size: 32px;
          font-weight: bold;
          margin-left: 10px;
        }
        .wy-app-modal-header-close {
          font-size: 24px;
          font-weight: bold;
          float: right;
          cursor: pointer;
          margin-top: 10px;
        }
        .wy-app-modal-header-close.disabled {
          pointer-events: none;
          color: #CCC;
        }
      }
    }

    .wy-app-modal-body {
      height: calc(100% - 80px);
      box-sizing: border-box;
      border-bottom-left-radius: 10px;
      border-bottom-right-radius: 10px;

      .el-tabs--left .el-tabs__header.is-left {
        width: 185px;
        margin-right: 0px;
      }
      .el-tabs__item {
        padding: 0px 20px 0px 20px;
        text-align: left;
      }
    }
  }
}
.no-data-msg {
  top: 100px !important;
  width: fit-content;
  background: #FBFCDB 0% 0% no-repeat padding-box;
  -webkit-box-shadow: 0px 2px 6px #0000000a;
  box-shadow: 0px 2px 6px #0000000a;
  border-radius: 8px;
  opacity: 1;
  // width: 390px;
  padding: 20px 0px 20px 0px;
  font-weight: normal;
  border: 1px solid #E1B878;

  .el-message__content {
    width: 660px;
    font-weight: 700;
    font-size: 15px;
  }

  .el-message__icon.el-icon-info {
    display: none;
  }

  .el-message__content {
    padding-right: 0px !important;
  }

  .el-message__content div {
    padding: 15px 30px 15px 55px;
    color: #8A5608;
  }
  .el-message__content .first-div {
    background-image: url('./assets/icons/icon_no_data_info.svg');
    background-size: 18px;
    background-position-x: 30px;
    background-position-y: 13px;
    background-repeat: no-repeat;
  }
  .el-message__content .second-div {
    background-image: url('./assets/icons/icon_no_data_check.svg');
    background-size: 18px;
    background-position-x: 30px;
    background-position-y: 13px;
    background-repeat: no-repeat;
  }

  .toAvailavleDateRange {
    text-decoration-line: underline;
    text-underline-offset: 5px;
    cursor: pointer;
    color: #8A5608;
    text-align: right;
    width: calc(100% - 30px);
    padding: 20px 30px 5px 0px !important;
    border-top: 1px solid;
    margin-top: 10px;
  }

  .el-message__closeBtn.el-icon-close {
    top: 15%;
    font-weight: 700;
  }
  .el-message__closeBtn {
    color: #606060;
  }
  .el-message__closeBtn:hover {
    color: #000;
  }
}
</style>
